import Vue from "vue";
import jwtDecode from "jwt-decode";
import {
  GetterTree,
  ActionTree,
  MutationTree,
  Commit,
  Dispatch,
  Store,
} from "vuex";
import { BrokenRule } from "@/shared/models/apiErrors";
import router from "@/router";

export interface JwtClaims {
  sid: string;
  tid: string;
  utid: string;
  exp: number;
  name: string;
  tname: string;
  roles: string;
}

export interface Tenant {
  id: string;
  name: string;
}

export interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  availableTenants: Tenant[];
}

export interface AuthSuccess {
  isSignedIn: boolean;
  errors: BrokenRule[];
}

export interface ForgottenPasswordResponse {
  resetEmailSent: boolean;
  errors: BrokenRule[];
}

export class AuthState {
  constructor() {
    this.storedUserResolved = new Promise(
      (x) => (this.storedUserResolvedDefferedCB = x)
    );
  }

  storedUserResolvedDefferedCB?: (value?: void | PromiseLike<void>) => void;
  storedUserResolved: Promise<void>;

  isSignedIn = false;

  claims?: JwtClaims = {
    sid: "",
    tid: "",
    utid: "",
    exp: 0,
    name: "",
    tname: "",
    roles: "",
  };

  serverData?: AuthResponse = {
    accessToken: "",
    refreshToken: "",
    availableTenants: [],
  };

  authHandle?: Promise<AuthSuccess>;
}

const storageKey = "pms.u";
const businessUnitStorageKey = "pms.bu";
const cashRegisterStorageKey = "pms.cr";

function tryExtractClaims(authResponse?: AuthResponse): JwtClaims | undefined {
  if (!authResponse || !authResponse.accessToken) {
    return;
  }

  try {
    const claims = jwtDecode(authResponse.accessToken) as JwtClaims;
    return claims;
  } catch {
    /* empty */
  }
}

const state = new AuthState();

const getters: GetterTree<AuthState, any> = {
  ready: (s) => s.storedUserResolved,
  isSignedIn: (s) => s.isSignedIn,
  accessToken: (s) => (s.serverData ? s.serverData.accessToken : undefined),
  availableTenants: (s) => (s.serverData ? s.serverData.availableTenants : []),
  claims: (s) => s.claims,
};

function loadState(): AuthResponse | undefined {
  const stored = localStorage.getItem(storageKey);

  if (stored) {
    try {
      const parsed = JSON.parse(stored) as AuthResponse;
      return parsed;
    } catch {
      /* empty */
    }
  }

  return;
}

function saveState({ serverData }: { serverData?: AuthResponse }) {
  const stringified = JSON.stringify(serverData);

  if (serverData) {
    if (localStorage.getItem(storageKey) != stringified) {
      localStorage.setItem(storageKey, stringified);
    }
  } else {
    localStorage.removeItem(storageKey);
  }
}

const actions: ActionTree<any, any> = {
  forgottenPassword(_, payload): Promise<ForgottenPasswordResponse> {
    const response = Vue.axios.post("/auth/ForgottenPassword", payload).then(() => {
      return { resetEmailSent: true, errors: [] };
    }).catch((errors) => {
      return { resetEmailSent: false, errors: (errors.data.brokenRules as BrokenRule[]) || [] };
    });

    return response;
  },
  resetPassword({ dispatch }, payload): Promise<AuthSuccess> {
    const response = Vue.axios.post("/auth/ResetPassword", payload).then(res => {
      return dispatch("signin", { username: res.data, password: payload.password });
    }).catch(errors => {
      return {
        isSignedIn: false,
        errors: (errors.data.brokenRules as BrokenRule[]) || [],
      };
    });

    return response;
  },
  signout({ commit }): Promise<AuthSuccess> {
    Vue.axios.post("auth/SignOut").then(() => {
      commit("setData", undefined);
      saveState({ serverData: undefined });
    });

    commit("calendar/reset", null, { root: true });
    commit("contingent/reset", null, { root: true });
    commit("businessUnit/reset", null, { root: true });
    commit("cashRegister/reset", null, { root: true });

    return Promise.resolve({ isSignedIn: false, errors: [] });
  },
  checkIfFirstSignin({ dispatch }, payload) {
    const response = Vue.axios.post("/auth/IsFirstSignin", payload).then(res => {
      if (!res.data) {
        return dispatch("signin", payload);
      }
      else {
        router.push({ name: "Reset password", query: { "token": res.data } });
        return { isSignedIn: false, errors: [] };
      }
    }).catch(errors => {
      return {
        isSignedIn: false,
        errors: (errors.data.brokenRules as BrokenRule[]) || [],
      };
    })

    return response;
  },
  signin(
    {
      state,
      commit,
      dispatch,
    }: { state: AuthState; commit: Commit; dispatch: Dispatch },
    payload
  ): Promise<AuthSuccess> {
    if (state.authHandle) {
      return state.authHandle;
    }

    const authCall = Vue.axios
      .post<AuthResponse>("/auth/Signin", payload)
      .then((response) => {
        commit("setData", response.data);
        saveState({ serverData: response.data });

        // call all the lists that need to be refreshed
        // on signin here
        dispatch("appParams/refreshAppParams", undefined, { root: true });
        dispatch("globalFilters/refreshFilter", undefined, { root: true });
        dispatch("financial/refreshModel", undefined, { root: true });
        dispatch("reports/refreshAccomodationUnitTypes", undefined, { root: true });

        return { isSignedIn: true, errors: [] };
      })
      .catch((errors) => {
        commit("setData", undefined);
        saveState({ serverData: undefined });
        return {
          isSignedIn: false,
          errors: (errors.data.brokenRules as BrokenRule[]) || [],
        };
      })
      .finally(() => commit("setAuthHandle", undefined));

    commit("setAuthHandle", authCall);

    return authCall;
  },
  refresh({
    state,
    dispatch,
  }: { state: AuthState; dispatch: Dispatch }, refreshGlobalFilters = true): Promise<AuthSuccess> {
    if (!state.claims) {
      return Promise.resolve({ isSignedIn: false, errors: [] });
    }

    return dispatch("refreshToTenant", { tenantId: +state.claims.tid, refreshGlobalFilters: refreshGlobalFilters });
  },
  refreshToTenant(
    {
      state,
      commit,
      dispatch,
    }: { state: AuthState; commit: Commit; dispatch: Dispatch },
    { tenantId, refreshGlobalFilters = true }
  ): Promise<AuthSuccess> {
    if (!state.serverData) {
      return Promise.resolve({ isSignedIn: false, errors: [] });
    }

    if (state.authHandle) {
      return state.authHandle;
    }

    let currentTenantId: number | null = null;
    if (state.claims) {
      currentTenantId = +state.claims.tid;
    }

    const authCall = Vue.axios
      .post("/auth/Refresh", {
        refreshToken: state.serverData.refreshToken,
        preferredTenantId: tenantId,
      })
      .then(({ data }: { data: AuthResponse }) => {
        commit("setData", data);
        saveState({ serverData: data });

        // call all the lists that need to be refreshed
        // on tenant change here
        dispatch("appParams/refreshAppParams", undefined, { root: true });
        dispatch("reports/refreshAccomodationUnitTypes", undefined, { root: true });

        if (refreshGlobalFilters || currentTenantId != tenantId) {
          dispatch("globalFilters/refreshFilter", undefined, { root: true });
        }

        return { isSignedIn: true, errors: [] };
      })
      .catch(({ data: { brokenRules } }) => {
        return {
          isSignedIn: false,
          errors: (brokenRules as BrokenRule[]) || [],
        };
      })
      .finally(() => commit("setAuthHandle", undefined));

    commit("setAuthHandle", authCall);

    return authCall;
  },
};

const mutations: MutationTree<any> = {
  setStoredResolved: (state: AuthState) => {
    if (state.storedUserResolvedDefferedCB) {
      state.storedUserResolvedDefferedCB();
    }
  },
  setData: (state: AuthState, payload?: AuthResponse) => {
    if (!payload) {
      state.serverData = {
        accessToken: "",
        refreshToken: "",
        availableTenants: [],
      };
      state.claims = undefined;
      state.isSignedIn = false;
    } else {
      state.serverData = { ...state.serverData, ...payload };
      state.claims = tryExtractClaims(payload);
      state.isSignedIn = (state.claims?.exp || 0) * 1000 > Date.now();
    }
  },
  setAuthHandle: (state: AuthState, payload?: Promise<AuthSuccess>) => {
    state.authHandle = payload;
  },
};

export const AuthPlugin = (store: Store<any>) => {
  const loaded = loadState();
  if (loaded) {
    store.commit("auth/setData", loaded);

    setTimeout(() => {
      store
        .dispatch("auth/refresh")
        .then(() => store.commit("auth/setStoredResolved"));
    }, 0);
  } else {
    store.commit("auth/setStoredResolved");
  }

  async function syncAuthState(ev: StorageEvent): Promise<any> {
    // when tenant is changed in another tab/window
    if (ev.key == storageKey) {
      const loaded = loadState();

      const oldTenantId = state.claims?.tid ?? 0;
      store.commit('auth/setData', loaded);
      const tenantId = state.claims?.tid ?? 0;
      const userId = state.claims?.sid ?? 0;

      if ((ev.oldValue && !ev.newValue) || (oldTenantId !== tenantId)) {
        // reset vuex store state
        store.commit("appParams/reset");
        store.commit("calendar/reset");
        store.commit("codebooks/reset");
        store.commit("contingent/reset");
        store.commit("documents/reset");
        store.commit("financial/reset");
        store.commit("reports/reset");
      }

      if (ev.oldValue && !ev.newValue) {
        router.push({ name: "Signin" });
        return;
      }

      if (oldTenantId !== tenantId) {
        store.dispatch("appParams/refreshAppParams");
        store.dispatch("reports/refreshAccomodationUnitTypes");

        const promises = [];
        promises.push(store.dispatch("globalFilters/refreshFilter"));
        promises.push(store.dispatch("financial/refreshModel"));
        promises.push(store.dispatch("businessUnit/refreshBusinessUnits"));
        promises.push(store.dispatch("cashRegister/refreshCashRegisters"));
        await Promise.all(promises);
        
        await store.dispatch("businessUnit/setBusinessUnitFromStorage", {
          tenantId,
          userId,
        });
        
        await store.dispatch("cashRegister/setCashRegisterFromStorage", {
          tenantId,
          userId,
        });

        if (router.currentRoute.path != "/dashboard") {
          await router.push({ path: "/dashboard" }).catch((err) => {
            //ignore vuex err regarding navigating to the page they are already on
            if (err.name != "NavigationDuplicated") {
              console.error(err);
            }
          });
        }
      }
    }

    // when business unit is changed in another tab/window
    if (ev.key == businessUnitStorageKey) {
      const tenantId = state.claims?.tid ?? 0;
      const userId = state.claims?.sid ?? 0;
      await store.dispatch("businessUnit/setBusinessUnitFromStorage", {
        tenantId,
        userId,
      });
    }
    
    // when cash register is changed in another tab/window
    if (ev.key == cashRegisterStorageKey) {
      const tenantId = state.claims?.tid ?? 0;
      const userId = state.claims?.sid ?? 0;
      await store.dispatch("cashRegister/setCashRegisterFromStorage", {
        tenantId,
        userId,
      });
    }
    
    //globalfilter change
    if (ev.key?.includes("_gfilter")) {
      const promises = [];
      promises.push(store.dispatch("globalFilters/refreshFilter"));
      Promise.all(promises);
    }
  }

  addEventListener("storage", syncAuthState);
};

export default {
  namespaced: true,
  state: () => state,
  getters,
  actions,
  mutations,
};
